import { FC, useEffect, useRef, useState } from "react"
import { handleAuthorizedRequest, trends } from "../../../../apis"
import { KSelectRangeDate, KSpan, KDropdown } from "kahuna-base-react-components"
import { formatDate, processPlatformOptions, toYYYYMMDD } from "../../../../utility"
import { User } from "../../../../types"
import { RootState } from "../../../../store"
import { connect } from "react-redux"
import { defaultEndDate, defaultStartDate, TREND_TRACK_EVENT_TYPE } from "../../../../constants"
import { DateRangeType } from "kahuna-base-react-components/dist/components/KSelectRangeDate/KSelectRangeDate"
import TopCountries from "./TopCountries"
import DemographicChart from "./DemographicChart"
import GenderDoughnutChart from "./GenderDoughnutChart"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { lang } from "../../../../constants/languages"
import { eventTypeOptions } from "../artist-overview/ArtistOverview"
import Loading from "../../../ui/Loading"

export interface AudienceAndDiscoveryData {
  trendCountryStreamMap?: { [key: string]: number }
  trendDemographicStreamMap?: { [key: string]: { [key: string]: number } }
  platformList: string[]
}

export type AudienceAndDiscoveryProps = {
  user: User
  dateValues: DateRangeType
}

export const socialTypeOptions = [
  { label: lang.trends.headers.videos_created, value: 1, value2:TREND_TRACK_EVENT_TYPE.SOCIAL_PRODUCTION },
  { label: lang.trends.headers.video_views, value: 2, value2: TREND_TRACK_EVENT_TYPE.SOCIAL_CONSUMPTION }
]

const AudienceAndDiscovery: FC<AudienceAndDiscoveryProps> = (props) => {
  const {dateValues} = props
  const [audienceAndDiscoveryData, setAudienceAndDiscoveryData] = useState<AudienceAndDiscoveryData>()
  //const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([])
  //const [selectedCountries, setSelectedCountries] = useState<string[]>([])
  const [selectedEventType, setSelectedEventType] = useState<string>(TREND_TRACK_EVENT_TYPE.DSP)
  const [platformOptions, setPlatformOptions] = useState<KSelectOption[]>([])
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([])
  const lastFetchedOptionsKeyRef = useRef<string>("")
  const [selectedSocialType, setSelectedSocialType] = useState<string>(TREND_TRACK_EVENT_TYPE.SOCIAL_CONSUMPTION)
  const [loading, setLoading] = useState(false)

  const fetchAudienceAndDiscovery = async (
    eventType: string,
    startDate: Date,
    endDate: Date,
    selectedPlatforms: string[],
    socialType: string
  ) => {
    const start_date = toYYYYMMDD(startDate)
    const end_date = toYYYYMMDD(endDate)

    let fetch_options = false
    const optionsKey = `${start_date}_${end_date}_${eventType}_${socialType}`
    if (lastFetchedOptionsKeyRef.current !== undefined && lastFetchedOptionsKeyRef.current !== optionsKey) {
      fetch_options = true
      lastFetchedOptionsKeyRef.current = optionsKey
    }

    const platformParam = selectedPlatforms.length > 0 ? `&platform_list=${selectedPlatforms.join(",")}` : ""
    const eventTypeParam = eventType !== "social" ? `&event_type=${eventType}` : `&event_type=${socialType}`

    setLoading(true)
    try {
      await handleAuthorizedRequest(async () => {
        const response = await trends(
          `/audience_and_discovery/?start_date=${start_date}&end_date=${end_date}${eventTypeParam}${platformParam}`,
          "get"
        )
        if (response && response.data) {
          const data = response.data
          const platforms: string[] = data?.platform_list || []
          setAudienceAndDiscoveryData({
            trendCountryStreamMap: data.trend_country_stream_map,
            trendDemographicStreamMap: data.trend_demographic_stream_map,
            platformList: platforms
          })
          if (fetch_options) {
            const sortedPlatforms = platforms?.sort((a, b) => a.localeCompare(b))
            const processedPlatformOptions = processPlatformOptions(sortedPlatforms)
            setPlatformOptions(processedPlatformOptions)
          }
        }
      }, "")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const dates = dateValues as Date[]
    if (!(dates[0] && dates[1])) return
    //const start =  new Date(dates[0].getTime() - 7 * DAY) // It is necessary to get the previous week data
    fetchAudienceAndDiscovery(selectedEventType, dates[0], dates[1], selectedPlatforms, selectedSocialType)
  }, [dateValues, selectedEventType, selectedPlatforms, selectedSocialType])
  
  if (loading) return <Loading />

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-row gap-5 my-10 items-center justify-between">
        <div className="flex flex-row items-center gap-3">
          <div className="flex flex-col gap-1 justify-center items-start">
            <KSpan text={lang.trends.filter.category} color="#111" fontWeight={500} />
            <KDropdown
              width={140}
              rightIcon="/analytics_icons/caret-down-new.svg"
              enableIndicator={true}
              options={eventTypeOptions}
              onSelect={(selected) => {
                setSelectedEventType((selected as KSelectOption)?.value2 || "")
                setSelectedPlatforms([])
                setSelectedSocialType(TREND_TRACK_EVENT_TYPE.SOCIAL_CONSUMPTION)
              }}
              selected={eventTypeOptions.find((option) => option.value2 === selectedEventType)}
            />
          </div>
          {selectedEventType === "social" && (
            <div className="flex flex-col gap-1 justify-center items-start">
              <KSpan text={lang.trends.filter.social_data_type} color="#111" fontWeight={500} />
              <KDropdown
                width={220}
                rightIcon="/analytics_icons/caret-down-new.svg"
                enableIndicator={true}
                options={socialTypeOptions}
                onSelect={(selected) => {
                  setSelectedSocialType((selected as KSelectOption)?.value2 || "")
                  setSelectedPlatforms([])
                }}
                selected={socialTypeOptions.find((option) => option.value2 === selectedSocialType)}
              />
            </div>
          )}
          <div className="flex flex-col gap-1 justify-center items-start">
            <KSpan text={lang.trends.filter.platforms} color="#111" fontWeight={500} />
            <KDropdown
              isClearable={true}
              isMulti={true}
              showOnlyIconsInMulti={true}
              width={200}
              rightIcon="/analytics_icons/caret-down-new.svg"
              enableIndicator={true}
              closeMenuOnSelect={false}
              options={platformOptions}
              onSelect={() => {}}
              onBlur={(selected) => {
                if (!selected) return
                const value2List = (selected as KSelectOption[])?.map((entity) => entity.value2 || "") || []
                const isSame =
                  value2List.length === selectedPlatforms.length &&
                  value2List.every((v, i) => v === selectedPlatforms[i])
                if (!isSame) {
                  setSelectedPlatforms(value2List)
                }
              }}
              placeholder={lang.trends.filter.all}
              selected={platformOptions.filter(
                (option) => option?.value2 && selectedPlatforms.includes(option?.value2)
              )}
            />
          </div>
        </div>
      </div>
      <div className="flex gap-3">
        <TopCountries trendCountryStreamMap={audienceAndDiscoveryData?.trendCountryStreamMap || {}} />
      </div>
      <div className="flex gap-3 max-[1200px]:!block">
        <div className="flex-1 min-h-0 h-[500px] max-[1200px]:w-full">
          <GenderDoughnutChart trendDemographicStreamMap={audienceAndDiscoveryData?.trendDemographicStreamMap || {}} />
        </div>
        <div className="flex-1 min-h-0 h-[500px] max-[1200px]:w-full max-[1200px]:mt-3">
          <DemographicChart trendDemographicStreamMap={audienceAndDiscoveryData?.trendDemographicStreamMap || {}} />
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    user: state.auth.user
  }
}

export default connect(mapStateToProps)(AudienceAndDiscovery)
